import {useCallback, useRef, useState} from 'react'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import {Navigate, useSearchParams} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {GetPermissions} from '../../../../modules/auth/core/Auth'
import {TopicLayout} from '../components/TopicLayout'
import TopicRequestForm, {type TopicRequestDraft} from '../components/TopicRequestForm'
import {getTopicForm} from '../data/topicForms'
import {getAvailableBriefingTopicGroups, type BriefingTopicGroup} from '../data/topics'

type Props = {group: BriefingTopicGroup}

export default function BriefingTopicGroupPage({group}: Props) {
  const permissions = GetPermissions()
  const [searchParams, setSearchParams] = useSearchParams()
  const [saving, setSaving] = useState(false)
  const drafts = useRef<Record<string, TopicRequestDraft>>({})
  const availableGroup = getAvailableBriefingTopicGroups(permissions).find(
    (item) => item.id === group.id
  )
  const requestedId = searchParams.get('topico')
  const topic =
    availableGroup?.topics.find((item) => item.id === requestedId) || availableGroup?.topics[0]
  const topicId = topic?.id
  const storeDraft = useCallback(
    (draft: TopicRequestDraft) => {
      if (topicId) drafts.current[topicId] = draft
    },
    [topicId]
  )

  if (!permissions?.length) return <TopBarProgress />
  if (!topic || (requestedId && !availableGroup.topics.some((item) => item.id === requestedId))) {
    return <Navigate to='/error/404' replace />
  }
  const definition = getTopicForm(topic.id)

  return (
    <TopicLayout>
      <PageTitle>{group.title}</PageTitle>
      <Box sx={{minWidth: 0, bgcolor: 'background.default', pb: 3}}>
        <Box sx={{maxWidth: 1120, width: '100%', mx: 'auto', minWidth: 0}}>
          <Box component='header' sx={{mb: 3}}>
            <Typography variant='caption' color='text.secondary' sx={{display: 'block', mb: 1.5}}>
              Briefing Promoções / Tópicos
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: '#eff6ff',
                  flexShrink: 0,
                }}
              >
                <img src={group.icon} alt='' width={28} height={28} aria-hidden='true' />
              </Box>
              <Box sx={{minWidth: 0}}>
                <Typography
                  component='h1'
                  sx={{
                    fontSize: 'clamp(20px, 2.2vw, 26px)',
                    fontWeight: 700,
                    lineHeight: 1.35,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {group.title}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{mt: 0.5}}>
                  {group.description}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            component='section'
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2.5,
              p: {xs: 2, sm: 3},
              mb: 3,
            }}
          >
            <Typography
              component='h2'
              id='topic-action-label'
              variant='subtitle1'
              sx={{fontWeight: 700}}
            >
              Tipo de solicitação
            </Typography>
            <Typography
              id='topic-action-help'
              variant='body2'
              color='text.secondary'
              sx={{mt: 0.5, mb: 2}}
            >
              Escolha o que deseja fazer para preencher ou consultar suas solicitações.
            </Typography>
            <RadioGroup
              name='topic-action'
              value={topic.id}
              aria-labelledby='topic-action-label'
              aria-describedby='topic-action-help'
              onChange={(_event, id) => {
                if (saving) return
                const next = new URLSearchParams(searchParams)
                next.set('topico', id)
                setSearchParams(next, {replace: true})
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'},
                gap: 1.25,
              }}
            >
              {availableGroup.topics.map((action) => (
                <FormControlLabel
                  key={action.id}
                  value={action.id}
                  disabled={saving}
                  control={<Radio size='small' sx={{p: 0.75, mr: 0.75}} />}
                  label={action.title}
                  sx={{
                    m: 0,
                    px: 1.25,
                    py: 1,
                    minWidth: 0,
                    minHeight: 52,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: action.id === topic.id ? 'primary.main' : 'divider',
                    bgcolor: action.id === topic.id ? '#eff6ff' : 'background.paper',
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      lineHeight: 1.5,
                      overflowWrap: 'anywhere',
                      fontWeight: action.id === topic.id ? 600 : 400,
                    },
                    '&:focus-within': {outline: '2px solid #2563eb', outlineOffset: 2},
                    '&:hover': {borderColor: 'primary.main'},
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
          <Typography component='h2' variant='subtitle1' sx={{fontWeight: 700, mb: 1.5}}>
            {topic.title}
          </Typography>
          {definition && (
            <TopicRequestForm
              key={topic.id}
              topic={topic}
              definition={definition}
              draft={drafts.current[topic.id]}
              onDraftChange={storeDraft}
              onSavingChange={setSaving}
            />
          )}
        </Box>
      </Box>
    </TopicLayout>
  )
}
